import React from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightsSummary } from 'dashboard/hooks/useInsightsSummary';

import * as Styled from 'dashboard/styles/Overview.style';
import * as Buttons from 'theme/components/Buttons.style';

const SKELETON_LINE_WIDTHS = ['100%', '92%', '96%', '64%'];

interface SummaryCardProps {
  queryParams: string;
}

export default function SummaryCard({
  queryParams,
}: SummaryCardProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');
  const { data, isLoading, isError, isFetching, refetch } =
    useInsightsSummary(queryParams);

  const isGenerating = isLoading || (isError && isFetching);

  const renderBody = (): JSX.Element => {
    if (isGenerating) {
      return (
        <Styled.SummarySkeletonBody aria-hidden="true">
          {SKELETON_LINE_WIDTHS.map((width) => (
            <Styled.SummarySkeletonLine key={width} $width={width} />
          ))}
        </Styled.SummarySkeletonBody>
      );
    }

    if (isError) {
      return (
        <Styled.SummaryError>
          <Styled.SummaryNote role="alert">
            {t('summary.error')}
          </Styled.SummaryNote>
          <Buttons.SecondaryLight
            type="button"
            $size="small"
            onClick={() => refetch()}
          >
            {t('summary.retry')}
          </Buttons.SecondaryLight>
        </Styled.SummaryError>
      );
    }

    if (!data?.summary) {
      return <Styled.SummaryNote>{t('summary.empty')}</Styled.SummaryNote>;
    }

    return <Styled.SummaryText>{data.summary}</Styled.SummaryText>;
  };

  return (
    <Styled.SummaryCardContainer aria-busy={isGenerating}>
      <Styled.CardHeader>
        <Styled.SummaryHeading>{t('summary.title')}</Styled.SummaryHeading>
      </Styled.CardHeader>
      {renderBody()}
    </Styled.SummaryCardContainer>
  );
}
